/**
 * Locale-aware relative timestamps ("2 hours ago").
 *
 * Uses date-fns, already a dependency and already used for relative time at
 * several call sites — none of which pass a locale, so they render English in
 * all ten languages. This module is the place that fixes that; those call sites
 * can adopt it later.
 *
 * Chosen over `Intl.RelativeTimeFormat`, which is zero-dep but only formats
 * `(value, unit)` — you hand-roll the "which bucket" logic date-fns already has
 * and the rest of the repo already trusts.
 */

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow, type Locale } from "date-fns";
import { da, de, enUS, es, fr, it, ja, nl, ptBR, zhCN } from "date-fns/locale";

/**
 * App language code → date-fns locale.
 *
 * Keys are the codes in SUPPORTED_LANGUAGES. `pt` maps to ptBR and `zh` to zhCN
 * to match the intlTags the app already declares (pt-BR, zh-CN).
 * `relative-time.test.ts` asserts every supported language has an entry, so
 * adding an eleventh language fails loudly rather than silently rendering English.
 */
export const DATE_FNS_LOCALES: Record<string, Locale> = {
  da,
  de,
  en: enUS,
  es,
  fr,
  it,
  ja,
  nl,
  pt: ptBR,
  zh: zhCN,
};

/**
 * Matches an ISO date-time string that carries NO timezone designator.
 *
 * Requires the time part, so a date-only `2026-09-20` is left alone: the spec parses that
 * form as UTC already, and appending Z would be a no-op at best.
 */
const NO_TIMEZONE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * Read an API timestamp as UTC when it does not say otherwise.
 *
 * The backend's `create_ts` / `read_ts` are Postgres TIMESTAMP columns holding UTC, and
 * graphene's DateTime scalar serializes a naive datetime through `isoformat()` — which emits
 * no offset. ECMAScript then parses an offset-less DATE-TIME string as LOCAL time (date-only
 * strings are the opposite, hence the regex above), so every reader silently shifted the row
 * by their own UTC offset: a viewer at UTC+1 saw rows an hour too old, and one at UTC-7 saw
 * them seven hours in the FUTURE — "in 7 hours" on a notification written minutes ago.
 *
 * The backend now sends an explicit +00:00, so this is a safety net rather than the fix. It
 * stays because the two repos deploy independently, rows written before that deploy are still
 * in the table, and being wrong about time is invisible until someone in another timezone
 * looks. Cheap to keep, and a string that already carries an offset is untouched.
 */
function parseApiTimestamp(iso: string): Date {
  return new Date(NO_TIMEZONE.test(iso.trim()) ? `${iso.trim()}Z` : iso);
}

/**
 * Format an ISO timestamp as relative time in the given language.
 *
 * Pure, and exported separately from the hook so it can be tested without
 * React. Returns null — rather than a placeholder string — for missing or
 * unparseable input, so callers decide what an absent timestamp looks like
 * instead of inheriting "Invalid Date".
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  lng: string,
): string | null {
  if (!iso) return null;

  const date = parseApiTimestamp(iso);
  if (Number.isNaN(date.getTime())) return null;

  return formatDistanceToNow(date, {
    addSuffix: true,
    locale: DATE_FNS_LOCALES[lng] ?? enUS,
  });
}

/**
 * Relative-time formatter bound to the current language.
 *
 * Re-created when the language changes so mounted rows re-render in the new
 * language. Deliberately does NOT tick on a timer: an interval living in an
 * always-mounted subtree is the bug class COMMON_BUGS.md warns about, and the
 * popover recomputes on open anyway.
 */
export function useRelativeTime(): (iso: string | null | undefined) => string | null {
  const { i18n } = useTranslation();
  return useCallback(
    (iso: string | null | undefined) => formatRelativeTime(iso, i18n.language),
    [i18n.language],
  );
}
